import React, { useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { t } from 'i18next';
import {
  Button,
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarRailOnly,
  SidebarSection,
  Tooltip,
  useHoverMenu,
} from '@datanomik/core-ui';
import { Search2Line } from '@datanomik/core-ui/icons';

import { PanelIcon } from './PanelIcon';
import { NavSubmenu } from '../nav/NavSubmenu';
import { navPill } from '../nav/NavPills';
import type { ShellNavEntry, ShellNavSection } from '../nav/types';

export interface SidebarChromeProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  sections: ShellNavSection[];
  brand?: React.ReactNode;
  account?: React.ReactNode;
  footerExtras?: React.ReactNode;
  onSearch?: () => void;
  searchLabel?: string;
  expandLabel?: string;
  collapseLabel?: string;
}

const FLYOUT_WIDTH = 232;

export const SidebarChrome: React.FC<SidebarChromeProps> = ({
  expanded,
  onToggleExpanded,
  sections,
  brand,
  account,
  footerExtras,
  onSearch,
  searchLabel = t('Search'),
  expandLabel = 'Expandir menu',
  collapseLabel = 'Recolher menu',
}) => {
  const flyout = useHoverMenu();
  const { pathname } = useLocation();
  const closeRef = useRef(flyout.close);
  closeRef.current = flyout.close;

  // A stale panel must not float over the page we just navigated to.
  React.useEffect(() => { closeRef.current(); }, [pathname]);

  const hoverProps = useCallback(
    (entry: ShellNavEntry) => {
      if (!entry.children?.length && !entry.loadChildren) return {};
      return {
        onPointerEnter: (e: React.PointerEvent<HTMLElement>) =>
          flyout.open(entry.key, e.currentTarget as HTMLElement),
        onPointerLeave: () => flyout.scheduleClose(),
        onFocus: (e: React.FocusEvent<HTMLElement>) =>
          flyout.openNow(entry.key, e.currentTarget as HTMLElement),
      };
    },
    [flyout],
  );

  const renderEntry = (entry: ShellNavEntry) => {
    const common = {
      icon: entry.icon,
      active: entry.active,
      endAdornment: navPill(entry),
      ...hoverProps(entry),
    };

    const row = entry.href ? (
      <SidebarItem
        key={entry.key}
        as="a"
        href={entry.href}
        target="_blank"
        rel="noreferrer"
        {...common}
      >
        {entry.label}
      </SidebarItem>
    ) : entry.to ? (
      <SidebarItem key={entry.key} as={Link} to={entry.to} {...common}>
        {entry.label}
      </SidebarItem>
    ) : (
      <SidebarItem key={entry.key} onClick={entry.onSelect} {...common}>
        {entry.label}
      </SidebarItem>
    );

    return (
      <React.Fragment key={entry.key}>
        <Tooltip label={expanded ? undefined : entry.label} side="right">
          {row}
        </Tooltip>
        <NavSubmenu
          entry={entry}
          anchor={flyout.anchor}
          open={flyout.openKey === entry.key}
          placement="right"
          width={FLYOUT_WIDTH}
          onKeepOpen={flyout.cancelClose}
          onLeave={flyout.scheduleClose}
          onClose={flyout.close}
        />
      </React.Fragment>
    );
  };

  return (
    <Sidebar expanded={expanded} data-modern>
      <SidebarHeader>
        <div className="min-w-0 flex-1" data-sidebar-expanded-only="">{brand}</div>
        {onSearch ? (
          <Tooltip label={searchLabel} side="bottom">
            <span data-sidebar-expanded-only="" className="shrink-0">
              <Button
                variant="ghost"
                size="icon"
                aria-label={searchLabel}
                onClick={onSearch}
                className="text-muted-foreground hover:text-foreground"
              >
                <Search2Line className="size-[19px]" />
              </Button>
            </span>
          </Tooltip>
        ) : null}
        <Tooltip label={expanded ? collapseLabel : expandLabel} side="right">
          <Button
            variant="ghost"
            size="icon"
            aria-label={expanded ? collapseLabel : expandLabel}
            aria-expanded={expanded}
            onClick={onToggleExpanded}
            className="text-muted-foreground hover:text-foreground"
          >
            <PanelIcon className="size-[19px]" />
          </Button>
        </Tooltip>
      </SidebarHeader>

      <SidebarBody>
        <SidebarRailOnly>
          {onSearch ? (
            <Tooltip label={searchLabel} side="right">
              <SidebarItem icon={<Search2Line />} onClick={onSearch} aria-label={searchLabel}>
                {searchLabel}
              </SidebarItem>
            </Tooltip>
          ) : null}
        </SidebarRailOnly>

        {sections.map((section) => (
          <SidebarSection key={section.key} title={section.label || undefined}>
            {section.entries.map(renderEntry)}
          </SidebarSection>
        ))}
      </SidebarBody>

      <SidebarFooter>
        {footerExtras}
        {account}
      </SidebarFooter>
    </Sidebar>
  );
};

export default SidebarChrome;
